import type { ComponentProps } from '@enonic/react-components'
import type { RichTextData } from '@enonic/react-components'
import * as React from 'react'
import {
  Section,
  Container,
  ResponsiveImage,
  Prose,
  ButtonLink,
} from '../primitives'
import type { ResponsiveImageData } from '../primitives'

export interface FeatureData extends Record<string, unknown> {
  heading: string
  body?: RichTextData
  linkUrl?: string
  linkLabel?: string
  linkNewTab: boolean
  linkAsButton: boolean
  image?: ResponsiveImageData
  mirrored: boolean
  backgroundColor: 'none' | 'light-blue' | 'light-peach' | 'dark-blue'
}

const bgMap: Record<FeatureData['backgroundColor'], string> = {
  none: '',
  'light-blue': 'bg-slate-100',
  'light-peach': 'bg-peach-100',
  'dark-blue': 'bg-blue-900',
}

export const Feature = (props: ComponentProps) => {
  const {
    heading,
    body,
    linkUrl,
    linkLabel,
    linkNewTab,
    linkAsButton,
    image,
    mirrored,
    backgroundColor,
  } = props.data as FeatureData

  const isDark = backgroundColor === 'dark-blue'
  const textColClass = image
    ? `col-span-full md:col-start-2 mb-12 md:mb-0 md:col-span-5 ${mirrored ? 'md:order-2 md:col-start-7' : ''}`
    : 'col-span-full md:col-start-3 md:col-span-8'

  const textLinkVariant = isDark ? 'text-link-white' : 'text-link'

  return (
    <Section background={bgMap[backgroundColor]}>
      <Container>
        <div className={`flex flex-col self-center gap-8 ${textColClass}`}>
          {heading && (
            <h2
              className={`text-3xl leading-normal ${isDark ? 'text-white' : 'text-blue-900'}`}
            >
              {heading}
            </h2>
          )}
          {body && (
            <Prose
              data={body}
              meta={props.meta}
              common={props.common}
              component={props.component}
              className={`prose-flush ${isDark ? 'prose-invert' : ''}`.trim()}
            />
          )}
          {linkLabel && linkUrl && (
            <ButtonLink
              href={linkUrl}
              label={linkLabel}
              newTab={linkNewTab}
              variant={linkAsButton ? 'button' : textLinkVariant}
            />
          )}
        </div>
        {image && (
          <div
            className={`col-span-full md:col-span-5 ${mirrored ? 'md:col-start-2 md:order-1' : ''}`}
          >
            <ResponsiveImage
              data={image}
              className="w-full object-cover md:h-full"
            />
          </div>
        )}
      </Container>
    </Section>
  )
}
